/**
 * SolutionRunCard — ESA panel card for an in-progress "New solution run" setup.
 *
 * Subscribes to solutionRunStore via useDebouncedRunState (400ms debounce)
 * for the display snapshot, so fast form edits don't thrash the panel.
 *
 * Actions (Review & initiate / Cancel) are rendered as suggestion chips in
 * EsaPanel — not as buttons inside this card — matching the design spec.
 */

import React, { useEffect, useState } from 'react';
import { StatusBadge, Typography } from '@growthaxl/web-foundation';
import { useDebouncedRunState } from '../../hooks/useDebouncedRunState';
import { getSolutionRunStore } from '../../../Canvas/store/solutionRunStore';
import { useEsaDomainStore } from '../../store/esaDomainStore';
import type { Guardrail } from '../../../Canvas/types/solutionRun';
import type { RunStatus } from '../../hooks/useSolutionRunActions';

// ── ESA spark icon (matches the design HTML spec) ────────────────────────────

function EsaSparkIcon(): React.ReactElement {
  return (
    <svg width="20" height="20" viewBox="0 0 28 28" fill="none" aria-hidden="true">
      <defs>
        <linearGradient id="solutionRunSparkGrad" x1="0" y1="0" x2="28" y2="28" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#6366f1" />
          <stop offset="1" stopColor="#a78bfa" />
        </linearGradient>
      </defs>
      <rect width="28" height="28" rx="10" fill="url(#solutionRunSparkGrad)" />
      <path d="M12 6.5 L13.4 11.6 L18.5 13 L13.4 14.4 L12 19.5 L10.6 14.4 L5.5 13 L10.6 11.6 Z" fill="#fff" />
      <path d="M20.5 6.5 V10.5 M18.5 8.5 H22.5" stroke="#fff" strokeWidth="1.7" strokeLinecap="round" />
      <circle cx="7.2" cy="20.5" r="1" fill="#fff" opacity="0.85" />
    </svg>
  );
}

// ── Guardrail list item ───────────────────────────────────────────────────────

function GuardrailItem({ guardrail }: { guardrail: Guardrail }): React.ReactElement {
  return (
    <li className="flex items-start gap-1.5 text-body-sm">
      <span className="text-border-strong mt-0.5 shrink-0">•</span>
      <span className="flex-1">{guardrail.text}</span>
      {guardrail.source === 'preset' && (
        <StatusBadge variant="info" size="sm" styleMode="soft">
          PRESET
        </StatusBadge>
      )}
    </li>
  );
}

// ── Row helper ───────────────────────────────────────────────────────────────

function SummaryRow({
  label,
  children,
  empty,
}: {
  label: string;
  children?: React.ReactNode;
  empty?: boolean;
}): React.ReactElement {
  return (
    <div className="flex items-baseline gap-3 py-0.5">
      {/* Fixed-width label column — must be a flex child (block), not wrapped in
          inline Typography, so that the width constraint is respected. */}
      <span className="w-[108px] shrink-0 text-[12px] font-semibold text-text-secondary leading-relaxed">
        {label}
      </span>
      {empty ? (
        <span className="flex-1 min-w-0 text-[12px] text-text-tertiary italic leading-relaxed">
          Left empty
        </span>
      ) : (
        <span className="flex-1 min-w-0 text-[12px] text-text-primary leading-relaxed">
          {children}
        </span>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export function SolutionRunCard(): React.ReactElement | null {
  const snapshot = useDebouncedRunState();

  const [runStatus, setRunStatus] = useState<RunStatus>(
    () => getSolutionRunStore().getState().status,
  );
  const [runConvId, setRunConvId] = useState<string | null>(
    () => getSolutionRunStore().getState().conversationId,
  );
  useEffect(() => getSolutionRunStore().subscribe(s => {
    setRunStatus(s.status);
    setRunConvId(s.conversationId);
  }), []);

  // Only show this card on the conversation bootstrapped for this run.
  // Hide while conversationId is still null — otherwise the card flashes on
  // the previous thread/briefing before ESA switches to the new conversation.
  const activeThreadId = useEsaDomainStore(s => s.activeThreadId);
  const isOnRunThread = runConvId !== null && runConvId === activeThreadId;

  if (!snapshot || !isOnRunThread) return null;

  const { solution, dataScope, objective, guardrails } = snapshot;
  const hasGuardrails = guardrails.length > 0;
  const isReviewing = runStatus === 'reviewing';

  return (
    <div
      data-testid="solution-run-card"
      className="border border-brand-weak rounded-8 overflow-hidden bg-gradient-to-b from-brand-weakest to-canvas text-body-sm"
    >
      {/* ── Header ── */}
      <div className="flex items-center gap-2 px-3 py-2.5 border-b border-border-default">
        <EsaSparkIcon />
        <Typography variant="subtitle2">New solution run</Typography>
        <Typography variant="caption" color="secondary">
          <span>· {solution.label}</span>
        </Typography>
        <div className="flex-1" />
        {isReviewing && (
          <span className="text-[10px] font-bold tracking-widest uppercase text-brand-on-primary bg-esa-accent px-[10px] py-[3px] rounded-full">
            Confirm
          </span>
        )}
      </div>

      {/* ── Body ── */}
      <div className="px-3 py-2.5 flex flex-col gap-0.5">
        <SummaryRow label="Solution">{solution.label}</SummaryRow>
        <SummaryRow label="Data scope" empty={!dataScope}>{dataScope}</SummaryRow>
        <SummaryRow label="Objective" empty={!objective}>{objective}</SummaryRow>

        {/* Guardrails bullet list — uses the same fixed label-column width as SummaryRow */}
        {hasGuardrails && (
          <div className="flex items-baseline gap-3 py-0.5">
            <span className="w-[108px] shrink-0 text-[12px] font-semibold text-text-secondary leading-relaxed">
              Guardrails
            </span>
            <ul className="flex-1 flex flex-col gap-1 list-none m-0 p-0" role="list">
              {guardrails.map(g => (
                <GuardrailItem key={g.id} guardrail={g} />
              ))}
            </ul>
          </div>
        )}

        {!hasGuardrails && <SummaryRow label="Guardrails" empty />}
      </div>

      {/* ── Footer hint ── */}
      <div className="px-3 pb-3">
        <Typography variant="caption" color="secondary">
          <em>
            {isReviewing
              ? 'Confirm or keep editing using the actions below ↓'
              : 'Use the actions below ↓ or answer in the conversation.'}
          </em>
        </Typography>
      </div>
    </div>
  );
}
